import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

// ── Localized copy ────────────────────────────────────────────────────
// Two states share this bundle: the beta-code entry form and the
// post-redemption success card. The success card explicitly tells the
// user to sign out and sign back in, matching the requested UX and
// giving the auth chain a clean re-hydration on their next session.
export const STRINGS = {
  "pt-BR": {
    brand: "English Job Interview Simulator",
    title: "Insira seu código Beta",
    intro: (n) => `Olá ${n}! Insira o código de acesso Beta que você recebeu para desbloquear o simulador.`,
    codeLabel: "Código de acesso Beta",
    submit: "Desbloquear",
    submitting: "Verificando…",
    success: "Beta desbloqueado!",
    logout: "Sair",
    successTitle: "Código Beta aceito!",
    successBody:
      "Seu código Beta foi validado e vinculado à sua conta. Para completar o desbloqueio, saia e entre novamente. Você não precisará inserir o código de novo.",
    successCta: "Sair e entrar novamente",
    attemptsRemaining: (n) =>
      n === 1
        ? "Você tem 1 tentativa restante antes que o acesso seja bloqueado."
        : `Você tem ${n} tentativas restantes antes que o acesso seja bloqueado.`,
    lockedTitle: "Muitas tentativas incorretas",
    lockedBody: (mins) =>
      `Por segurança, o acesso foi bloqueado por ${mins} minutos. Aguarde e tente novamente.`,
    lockedWait: (mm, ss) => `Tente novamente em ${mm}:${String(ss).padStart(2, "0")}.`,
    errors: {
      invalid: "Este código de acesso Beta não é válido. Confira a digitação e tente novamente.",
      disabled: "Este código de acesso Beta foi desativado pelo administrador.",
      used: "Este código de acesso Beta já foi utilizado.",
      expired: "Este código de acesso Beta expirou.",
      email_mismatch: "Este código de acesso Beta foi atribuído a outro e-mail.",
      race: "Este código acabou de ser usado por outra pessoa. Tente outro código.",
    },
  },
  en: {
    brand: "English Job Interview Simulator",
    title: "Enter your Beta code",
    intro: (n) => `Hi ${n}! Enter the Beta access code you received to unlock the simulator.`,
    codeLabel: "Beta access code",
    submit: "Unlock",
    submitting: "Verifying…",
    success: "Beta unlocked!",
    logout: "Sign out",
    successTitle: "Beta access code accepted!",
    successBody:
      "Your Beta code has been validated and linked to your account. To finish unlocking the app, please sign out and sign back in. You won't need to enter the code again.",
    successCta: "Sign out and sign back in",
    attemptsRemaining: (n) =>
      n === 1
        ? "You have 1 attempt left before your access is temporarily locked."
        : `You have ${n} attempts left before your access is temporarily locked.`,
    lockedTitle: "Too many incorrect attempts",
    lockedBody: (mins) =>
      `For your security, we've paused code entry for ${mins} minutes. Please wait and then try again.`,
    lockedWait: (mm, ss) => `Try again in ${mm}:${String(ss).padStart(2, "0")}.`,
    errors: {
      invalid: "This Beta access code isn't valid. Please check the spelling and try again.",
      disabled: "This Beta access code has been disabled by the administrator.",
      used: "This Beta access code has already been used.",
      expired: "This Beta access code has expired.",
      email_mismatch: "This Beta access code was assigned to a different email address.",
      race: "This code was just used by someone else. Please try a different code.",
    },
  },
  "es-419": {
    brand: "English Job Interview Simulator",
    title: "Ingrese su código Beta",
    intro: (n) => `¡Hola ${n}! Ingrese el código de acceso Beta que recibió para desbloquear el simulador.`,
    codeLabel: "Código de acceso Beta",
    submit: "Desbloquear",
    submitting: "Verificando…",
    success: "¡Beta desbloqueada!",
    logout: "Salir",
    successTitle: "¡Código Beta aceptado!",
    successBody:
      "Su código Beta se validó y quedó vinculado a su cuenta. Para completar el desbloqueo, cierra sesión e inicie sesión de nuevo. No tendrá que volver a ingresar el código.",
    successCta: "Cerrar sesión e iniciar sesión de nuevo",
    attemptsRemaining: (n) =>
      n === 1
        ? "Le queda 1 intento antes de que se bloquee el acceso."
        : `Le quedan ${n} intentos antes de que se bloquee el acceso.`,
    lockedTitle: "Demasiados intentos incorrectos",
    lockedBody: (mins) =>
      `Por seguridad, pausamos la entrada del código por ${mins} minutos. Espera y vuelve a intentarlo.`,
    lockedWait: (mm, ss) => `Intenta de nuevo en ${mm}:${String(ss).padStart(2, "0")}.`,
    errors: {
      invalid: "Este código de acceso Beta no es válido. Revise la escritura e inténtalo de nuevo.",
      disabled: "Este código de acceso Beta fue desactivado por el administrador.",
      used: "Este código de acceso Beta ya fue utilizado.",
      expired: "Este código de acceso Beta expiró.",
      email_mismatch: "Este código de acceso Beta fue asignado a otro correo electrónico.",
      race: "Este código acaba de ser usado por otra persona. Intenta con otro código.",
    },
  },
  "fr-CA": {
    brand: "English Job Interview Simulator",
    title: "Saisis votre code Beta",
    intro: (n) => `Bonjour ${n}! Saisis le code d’accès Beta que vous avez reçu pour débloquer le simulateur.`,
    codeLabel: "Code d’accès Beta",
    submit: "Débloquer",
    submitting: "Vérification…",
    success: "Beta débloqué!",
    logout: "Se déconnecter",
    successTitle: "Code Beta accepté!",
    successBody:
      "Votre code Beta a été validé et associé à votre compte. Pour finaliser le déblocage, déconnecte-vous puis reconnecte-vous. Vous n’auras plus besoin de saisir le code.",
    successCta: "Se déconnecter et se reconnecter",
    attemptsRemaining: (n) =>
      n === 1
        ? "Il vous reste 1 essai avant que l’accès soit temporairement bloqué."
        : `Il vous reste ${n} essais avant que l’accès soit temporairement bloqué.`,
    lockedTitle: "Trop de tentatives incorrectes",
    lockedBody: (mins) =>
      `Par sécurité, la saisie du code est bloquée pendant ${mins} minutes. Attends puis réessaie.`,
    lockedWait: (mm, ss) => `Réessayez dans ${mm}:${String(ss).padStart(2, "0")}.`,
    errors: {
      invalid: "Ce code d’accès Beta n’est pas valide. Vérifiez l’orthographe et réessaie.",
      disabled: "Ce code d’accès Beta a été désactivé par l’administrateur.",
      used: "Ce code d’accès Beta a déjà été utilisé.",
      expired: "Ce code d’accès Beta a expiré.",
      email_mismatch: "Ce code d’accès Beta a été attribué à une autre adresse courriel.",
      race: "Ce code vient d’être utilisé par quelqu’un d’autre. Essaie un autre code.",
    },
  },
};

function pickStrings(lang) {
  return STRINGS[lang] || STRINGS.en;
}

export default function WaitingBeta() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const t = pickStrings(user?.preferred_language);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // `redeemed` is true only for THIS render cycle — set right after the
  // POST /auth/redeem-beta-code call returns success. It gates the
  // "please sign out and sign back in" confirmation panel.
  const [redeemed, setRedeemed] = useState(false);
  // Attempts remaining before the account is temporarily locked. `null`
  // means we haven't heard from the server yet; the counter appears as
  // soon as the first wrong code is submitted.
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  // When set (>0), the beta-code input is locked and a countdown is
  // shown. Value is the number of seconds until re-entry is allowed.
  const [lockSecondsLeft, setLockSecondsLeft] = useState(0);
  const locked = lockSecondsLeft > 0;

  // Ticks the lockout countdown every second while locked.
  useEffect(() => {
    if (lockSecondsLeft <= 0) return undefined;
    const id = setInterval(() => {
      setLockSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [lockSecondsLeft]);

  // Escape hatch: if the user LANDS on this page and their account is
  // already beta-approved (e.g. they signed out after redeeming, then
  // signed back in and Login.jsx sent them to `returnTo=/waiting-beta`
  // — see the sign-out cycle bug), bounce them straight to the app so
  // they don't get stuck re-entering an already-redeemed code.
  // We only auto-redirect when `redeemed` is false so the success
  // confirmation panel remains visible after a fresh redemption.
  useEffect(() => {
    if (!redeemed && user?.beta_access_approved) {
      navigate("/", { replace: true });
    }
  }, [redeemed, user?.beta_access_approved, navigate]);

  const redeem = async (e) => {
    e.preventDefault();
    setErr("");
    if (!code.trim() || locked) return;
    setBusy(true);
    try {
      await api.post("/auth/redeem-beta-code", { code: code.trim() });
      toast.success(t.success);
      setRedeemed(true);
      setAttemptsLeft(null);
      try { await refresh(); } catch { /* swallow — success UI still shows */ }
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      // Structured lock-out from the backend: freeze the form for the
      // remaining window and start a live countdown.
      if (status === 429 && detail && detail.code === "locked_out") {
        setLockSecondsLeft(Math.max(1, Number(detail.retry_after_seconds) || 0));
        setAttemptsLeft(0);
        setErr("");
        return;
      }
      // Structured "wrong code" family (400 / 409). We keep the input
      // populated so the user can correct a small typo, and surface how
      // many attempts remain before lock-out. The visible copy is picked
      // from the LOCALIZED bundle keyed by the backend `detail.code`
      // (invalid / disabled / used / expired / email_mismatch / race),
      // so PT-BR / ES-419 / FR-CA users get their language even though
      // the backend response body is English.
      if (
        detail && typeof detail === "object" &&
        "attempts_remaining" in detail
      ) {
        setAttemptsLeft(Number(detail.attempts_remaining) || 0);
        const localized = (t.errors && t.errors[detail.code]) || null;
        setErr(localized || detail.message || formatApiErrorDetail(ex));
        return;
      }
      // Fallback for anything unexpected.
      setErr(
        (detail && typeof detail === "object" && detail.message) ||
          formatApiErrorDetail(ex),
      );
    } finally {
      setBusy(false);
    }
  };

  const signOutAndReset = async () => {
    // Kick off the AuthContext logout — it clears the server session,
    // wipes local state, and ProtectedRoute will redirect to /login
    // automatically because `status` becomes "unauthenticated".
    await logout();
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-[color:var(--bg-base)]">
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-xl border border-[color:var(--border-stone)] p-6 sm:p-8">
        <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)] mb-2">
          {t.brand}
        </div>

        {redeemed ? (
          // ── Success confirmation panel ─────────────────────────────
          // Explicitly instructs the user to sign out and sign back in.
          <div data-testid="beta-success-panel">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 shrink-0 rounded-full bg-[color:var(--brand)]/10 text-[color:var(--brand)] flex items-center justify-center">
                <CheckCircle weight="duotone" size={28} />
              </div>
              <div className="min-w-0">
                <h1
                  className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--text-primary)] leading-tight"
                  data-testid="beta-success-title"
                >
                  {t.successTitle}
                </h1>
                <p
                  className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed"
                  data-testid="beta-success-body"
                >
                  {t.successBody}
                </p>
              </div>
            </div>
            <div className="mt-6">
              <button
                type="button"
                onClick={signOutAndReset}
                className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium"
                data-testid="beta-success-signout"
              >
                {t.successCta}
              </button>
            </div>
          </div>
        ) : (
          // ── Beta-code entry form ───────────────────────────────────
          <>
            <h1
              className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--text-primary)]"
              data-testid="waiting-beta-title"
            >
              {t.title}
            </h1>
            <p className="mt-3 text-sm text-[color:var(--text-secondary)]">
              {t.intro(user?.first_name || user?.email || "")}
            </p>

            <form onSubmit={redeem} className="mt-4 space-y-3">
              <label className="block">
                <span className="block text-xs font-medium text-[color:var(--text-secondary)] mb-1">
                  {t.codeLabel}
                </span>
                <input
                  type="text"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  disabled={locked}
                  className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm font-mono uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)] disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[color:var(--bg-surface-hover)]"
                  placeholder="XXXX-XXXX-XXXX"
                  autoComplete="one-time-code"
                  data-testid="beta-code-input"
                />
              </label>

              {locked ? (
                // Full lock-out banner replaces the inline error and the
                // remaining-attempts hint.
                <div
                  className="rounded-xl border border-[color:var(--score-bad)]/40 bg-[color:var(--score-bad)]/5 p-3"
                  data-testid="beta-locked-banner"
                  role="alert"
                >
                  <div className="font-semibold text-sm text-[color:var(--score-bad)] mb-1">
                    {t.lockedTitle}
                  </div>
                  <p className="text-xs text-[color:var(--text-primary)] leading-relaxed">
                    {t.lockedBody(15)}
                  </p>
                  <p
                    className="mt-2 text-xs font-mono text-[color:var(--text-secondary)]"
                    data-testid="beta-locked-countdown"
                  >
                    {t.lockedWait(
                      Math.floor(lockSecondsLeft / 60),
                      lockSecondsLeft % 60,
                    )}
                  </p>
                </div>
              ) : (
                <>
                  {err && (
                    <div
                      className="rounded-lg bg-[color:var(--score-bad)]/10 border border-[color:var(--score-bad)]/30 p-2 text-xs text-[color:var(--score-bad)]"
                      data-testid="beta-code-error"
                    >
                      {err}
                    </div>
                  )}
                  {attemptsLeft !== null && attemptsLeft > 0 && (
                    <div
                      className="text-xs text-[color:var(--text-secondary)]"
                      data-testid="beta-attempts-remaining"
                    >
                      {t.attemptsRemaining(attemptsLeft)}
                    </div>
                  )}
                </>
              )}

              <button
                type="submit"
                disabled={busy || locked}
                className="w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="beta-code-submit"
              >
                {busy ? t.submitting : t.submit}
              </button>
            </form>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={logout}
                className="rounded-full border border-[color:var(--border-stone)] px-4 py-2 text-sm hover:bg-[color:var(--bg-surface-hover)]"
                data-testid="waiting-beta-logout"
              >
                {t.logout}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
